import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  useTheme,
  alpha,
  Button,
  Paper,
  Alert,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  CampaignRounded,
  AttachMoneyRounded,
  SettingsRounded,
  DescriptionRounded,
  TrendingUpRounded,
  PeopleRounded,
  ShieldRounded,
  ReceiptLongRounded,
  AutoAwesomeRounded,
  MenuRounded,
} from "@mui/icons-material";
import { useAIActivation } from "./AIActivationContext";

import DocAiChat from "./AiManagers/DocAiChat";
import MarkAiChat from "./AiManagers/MarkAiChat";
import FinAiChat from "./AiManagers/FinAiChat";
import OperAiChat from "./AiManagers/OperAiChat";
import ParraAiChat from "./AiManagers/ParraAiChat";
import SalesAiChat from "./AiManagers/SalesAiChat";
import HrraAiChat from "./AiManagers/HrraAiChat";
import ComplaiChat from "./AiManagers/ComplaiChat";
import { useNavigate } from "react-router-dom";

const aiConfig = {
  Markai: {
    icon: CampaignRounded,
    color: "#ff6b6b",
    description: "Marketing Assistant",
    component: MarkAiChat,
  },
  Fincai: {
    icon: AttachMoneyRounded,
    color: "#4ecdc4",
    description: "Financial Advisor",
    component: FinAiChat,
  },
  Operai: {
    icon: SettingsRounded,
    color: "#45b7d1",
    description: "Operations Manager",
    component: OperAiChat,
  },
  Parrai: {
    icon: DescriptionRounded,
    color: "#96ceb4",
    description: "Paralegal Assistant",
    component: ParraAiChat,
  },
  Salesai: {
    icon: TrendingUpRounded,
    color: "#ffeaa7",
    description: "Sales Strategist",
    component: SalesAiChat,
  },
  Hrrai: {
    icon: PeopleRounded,
    color: "#dfe6e9",
    description: "HR Consultant",
    component: HrraAiChat,
  },
  Complai: {
    icon: ShieldRounded,
    color: "#fab1a0",
    description: "Compliance Officer",
    component: ComplaiChat,
  },
  Docai: {
    icon: ReceiptLongRounded,
    color: "#a8e6cf",
    description: "Document Specialist",
    component: DocAiChat,
  },
};

const RenderAIChat = ({ selectedAI }) => {
  if (!selectedAI) return null;

  const ChatComponent = selectedAI.component;

  if (!ChatComponent) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Chat component not found for {selectedAI.name}
        </Alert>
      </Box>
    );
  }

  return <ChatComponent activeAI={selectedAI} />;
};

const AIWorkspace = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { activatedAIs, isAIActivated } = useAIActivation();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [selectedAI, setSelectedAI] = useState(null);

  const activeAIs = Object.keys(aiConfig)
    .filter((aiName) => {
      const isActive = isAIActivated(aiName);
      return isActive;
    })
    .map((aiName) => ({
      name: aiName,
      ...aiConfig[aiName],
    }));

  useEffect(() => {
    if (activeAIs.length > 0 && !selectedAI) {
      setSelectedAI(activeAIs[0]);
    }
  }, [activeAIs, selectedAI]);

  const handleSelectAI = (ai) => {
    setSelectedAI(ai);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  if (activeAIs.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100%",
          p: 4,
          textAlign: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: "2rem",
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            maxWidth: 400,
          }}
        >
          <AutoAwesomeRounded
            sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }}
          />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            No AI Assistants Activated
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Activate AI assistants from the AI Employees page to start using
            them in your workspace.
          </Typography>
          <Alert severity="info" sx={{ mb: 2, textAlign: "left" }}>
            <Typography variant="body2">
              Activated AIs from context: {activatedAIs.join(", ") || "None"}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate("/dashboard/ai")}
            sx={{ borderRadius: "2rem", mt: 2, textTransform: "capitalize" }}
          >
            Go to AI Managers
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "89vh",
        width: "100%",
        position: "relative",
        bgcolor: "#ffffff",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      {/* AI Manager Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={drawerOpen}
        sx={{
          width: 280,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: "#ffffff",
            position: "relative",
            height: "100%",
            boxShadow: "none",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
              px: 1,
            }}
          >
            <Typography variant="body1" fontWeight={600} color="text.primary">
              AI Managers
            </Typography>
            <IconButton
              onClick={toggleDrawer}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Box>

          <List
            sx={{
              flex: 1,
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
            }}
          >
            {activeAIs.map((ai) => {
              const Icon = ai.icon;
              const isSelected = selectedAI?.name === ai.name;

              return (
                <ListItem key={ai.name} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => handleSelectAI(ai)}
                    sx={{
                      borderRadius: "12px",
                      py: 1.5,
                      "&.Mui-selected": {
                        bgcolor: alpha(ai.color, 0.1),
                        "&:hover": {
                          bgcolor: alpha(ai.color, 0.15),
                        },
                      },
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <Avatar
                        sx={{
                          bgcolor: ai.color,
                          width: 36,
                          height: 36,
                        }}
                      >
                        <Icon sx={{ fontSize: 18, color: "#fff" }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={ai.name}
                      secondary={ai.description}
                      primaryTypographyProps={{
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: "0.95rem",
                        color: isSelected ? ai.color : "text.primary",
                      }}
                      secondaryTypographyProps={{
                        variant: "caption",
                        sx: { color: "text.secondary", fontSize: "0.75rem" },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>

      {/* Main Chat Area */}
      <Box
        sx={{
          flexGrow: 1,
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ml: drawerOpen ? 0 : -28,
          height: "100%",
          overflow: "hidden",
          bgcolor: "#f9fafc",
          position: "relative",
        }}
      >
        {selectedAI ? (
          <Box sx={{ height: "100%", overflow: "auto" }}>
            <RenderAIChat selectedAI={selectedAI} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <AutoAwesomeRounded
              sx={{
                fontSize: 48,
                color: alpha(theme.palette.primary.main, 0.3),
              }}
            />
            <Typography color="text.secondary" variant="body1">
              Select an AI assistant to start chatting
            </Typography>
          </Box>
        )}
      </Box>

      {/* Toggle button when drawer is closed */}
      {!drawerOpen && (
        <IconButton
          onClick={toggleDrawer}
          sx={{
            position: "absolute",
            left: "0.5rem",
            top: "2rem",
            transform: "translateY(-50%)",
            bgcolor: "#ffffff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            "&:hover": {
              bgcolor: "#ffffff",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            },
            zIndex: 1200,
            width: 40,
            height: 40,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <MenuRounded fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default AIWorkspace;
